import React, { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { bindActionCreators } from 'redux'
import cn from 'classnames'
import { Button, Divider, Grid, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import uiActionCreators from 'State/Store/UI/UI.actions'
import themeCreateInjectableStyles from 'Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support'
import NotificationConfigComp from '../NotificationConfig/NotificationConfig.component'
import Spinner from '../../Spinner/Spinner.component'
import notificationConfigsStyles from './NotificationConfigs.component.styles'
import { NotificationConfig, formatNotifcationTypeHeadline, formatNotifcationTypeDescription } from 'types/NotificationConfig'
import ConfirmMobileNumberModal from '../../ConfirmMobileNumberModal/ConfirmMobileNumberModal.component'
import EditMobileNumberModal from '../../EditMobileNumberModal/EditMobileNumberModal.component'
import userProfileCurrentSelector from 'State/Store/UserProfile/Selectors/UserProfileCurrent.selector'
import withServices from 'Contexts/Services/WithServices.context'

const MOBILE_NUMBER_ALREADY_CONFIRMED_ON_THIS_DEVICE = 'MOBILE_NUMBER_ALREADY_CONFIRMED_ON_THIS_DEVICE'

interface Props {
  belongsToId: string,
  onChange(configs: NotificationConfig[]): void,
  onSave(): void,
  configs: NotificationConfig[],
  isSaving: boolean,
  hasErrorSaving: boolean,
}
interface InjectedProps {
  classes: any,
  actions: any,
  userProfile: any,
  services: any,
}

const NotificationConfigs = (props: Props & InjectedProps) => {
  const {
    belongsToId,
    onChange,
    onSave,
    configs = [],
    isSaving,
    hasErrorSaving,

    classes,
    userProfile,
    services,
  } = props
  const [ confirmMobileNumberVisible, setConfirmMobileNumberVisible ] = useState<boolean>(false)
  const [ editMobileNumberVisible, setEditMobileNumberVisible ] = useState<boolean>(false)
  const [ openAddModal, setOpenAddModal ] = useState<boolean>(false)
  const originalConfig = useRef<string>('')
  const [ stashedConfigAndIndex, setStashedConfigAndIndex ] = useState<{
    config: NotificationConfig,
    index: number,
  }>(null as any)

  useEffect(() => {
    if (!originalConfig.current && configs.length) {
      originalConfig.current = JSON.stringify(configs);
    }
  }, [configs]);

  useEffect(() => {
    originalConfig.current = '';
  }, []);

  const handleConfigChange = (index: number) => async (config: NotificationConfig) => {
    const currentSMSStatus = configs[index].values.sms
    const upcomingSMSStatus = config.values.sms
    if (!currentSMSStatus && upcomingSMSStatus) {
      if (!userProfile.mobilePhone) {
        setStashedConfigAndIndex({ config, index })
        setOpenAddModal(true)
      } else {
        const alreadyConfirmedOnce = await services.storageService.getItem(MOBILE_NUMBER_ALREADY_CONFIRMED_ON_THIS_DEVICE)
        if (alreadyConfirmedOnce) {
          performChange(config, index)
        } else {
          setStashedConfigAndIndex({ config, index })
          setConfirmMobileNumberVisible(true)
        }
      }
    } else {
      performChange(config, index)
    }
  }
  const performChange = (config: NotificationConfig, index: number) => {
    if (!config) return
    onChange([
      ...configs.slice(0, index),
      config,
      ...configs.slice(index + 1),
    ])
  }

  const onSaveHandler = () => {
    onSave()
    originalConfig.current = '';
  }

  const onDenyMobileNumber = () => {
    setEditMobileNumberVisible(true)
  }

  const onConfirmMobileNumber = () => {
    services.storageService.setItem(MOBILE_NUMBER_ALREADY_CONFIRMED_ON_THIS_DEVICE, true)
    setConfirmMobileNumberVisible(false)
    if (stashedConfigAndIndex) {
      performChange(stashedConfigAndIndex.config, stashedConfigAndIndex.index)
    }
  }

  const handleEditSuccess = () => {
    services.storageService.setItem(MOBILE_NUMBER_ALREADY_CONFIRMED_ON_THIS_DEVICE, true)
    setOpenAddModal(false)
    setEditMobileNumberVisible(false)
    setConfirmMobileNumberVisible(false)
    if (stashedConfigAndIndex) {
      performChange(stashedConfigAndIndex.config, stashedConfigAndIndex.index)
    }
  }

  const handleEditCancel = () => {
    setOpenAddModal(false)
    setEditMobileNumberVisible(false)
    setConfirmMobileNumberVisible(false)
  }

  const isOriginalConfig = (config: NotificationConfig[], originalConfig: string) => JSON.stringify(config) === originalConfig;

  /**
   * Render the save button
   * @param {boolean} isTopButton should button be rendered at the top of container
   * @param {string} label text to be displayed on the button
   * @returns {React.Component} The rendered button
   * @memberof NotificationConfigs
   */
  const renderSaveButton = (label: string, isTopButton: boolean) => (
    <Button className={isTopButton ? classes.topButton : null} variant='contained' disabled={isSaving || isOriginalConfig(configs, originalConfig.current) || (!hasErrorSaving && !originalConfig.current)} color='primary' onClick={onSaveHandler}>
      {isSaving ? <Spinner loaderStyles={classes.spinner} size={16} /> : label}
    </Button>);

  return (
    <React.Fragment>
      <EditMobileNumberModal
        title={'Add Mobile Number'}
        extraInfo={`To switch on SMS notifications, add your mobile phone number to your profile.`}
        open={openAddModal}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />
      <EditMobileNumberModal
        title={'Edit Mobile Number'}
        open={editMobileNumberVisible}
        mobileNumber={userProfile.mobilePhone}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />
      <ConfirmMobileNumberModal
        open={confirmMobileNumberVisible}
        onYes={onConfirmMobileNumber}
        onNo={onDenyMobileNumber}
        mobileNumber={userProfile.mobilePhone}
      />
      {renderSaveButton(`SAVE`, true)}
      <Grid container spacing={4}>
        <Grid item xs={12}>
          {` `}
          <Grid container spacing={1} justify='flex-end'>
            <Grid item>
              <Typography variant='body2' className={classes.configsHeading}>
                In App
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='body2' className={classes.configsHeading}>
                SMS
              </Typography>
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          {
            configs.length !== 0
            && configs.map((config, index) => (
              <React.Fragment key={config.type + belongsToId}>
                <NotificationConfigComp
                  key={config.type}
                  config={config}
                  headline={formatNotifcationTypeHeadline(config.type)}
                  description={formatNotifcationTypeDescription(config.type)}
                  onChange={handleConfigChange(index)}
                />
                <Divider className={classes.divider} />
              </React.Fragment>
            ))
          }
        </Grid>
        <Grid item xs={12} className={cn(classes.textAlignRight)}>
          {renderSaveButton(`SAVE SETTINGS`, false)}
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default compose<Props & InjectedProps, Props>(
  withStyles(themeCreateInjectableStyles(notificationConfigsStyles) as any, { withTheme: true }),
  withServices,
  connect(
    (state: any) => ({
      userProfile: userProfileCurrentSelector(state),
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...uiActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(NotificationConfigs)
